import pick from 'lodash/pick';
import { types as sdkTypes } from '../../util/sdkLoader';
import { storableError } from '../../util/errors';
import { addMarketplaceEntities } from '../../ducks/marketplaceData.duck';
import { transactionLineItems } from '../../util/api';
import * as log from '../../util/log';
import { denormalisedResponseEntities } from '../../util/data';
import { resetToStartOfDay } from '../../util/dates';
import {
  LISTING_PAGE_DRAFT_VARIANT,
  LISTING_PAGE_PENDING_APPROVAL_VARIANT,
  LISTING_PAGE_CLOSED_VARIANT,
} from '../../util/urlHelpers';
import { fetchCurrentUser, fetchCurrentUserHasOrdersSuccess } from '../../ducks/user.duck';
import api from '../../api';
import { LISTING_STATE_PUBLISHED } from '../../util/types';
import { getLocationQuery, locationBounds } from '../../util/maps';
import { fetchSessions } from '../../ducks/Bookings.duck';
import { parseListingToSharetribe } from '../../util/sharetribeParser';
import defaultConfig from '../../config/configDefault';
import { dayCountAvailableForBooking } from '../../config/configStripe';
import { transitions } from '../../transactions/transactionProcessBooking';
import { getPartnerDataForEntites } from '../../util/partner';
import { set } from 'lodash';

const { UUID } = sdkTypes;

const REVIEWS_PAGE_SIZE = 10;

// ================ Action types ================ //

export const SET_INITIAL_VALUES = 'app/ListingPage/SET_INITIAL_VALUES';

export const SHOW_LISTING_REQUEST = 'app/ListingPage/SHOW_LISTING_REQUEST';
export const SHOW_LISTING_ERROR = 'app/ListingPage/SHOW_LISTING_ERROR';

export const FETCH_REVIEWS_REQUEST = 'app/ListingPage/FETCH_REVIEWS_REQUEST';
export const FETCH_REVIEWS_SUCCESS = 'app/ListingPage/FETCH_REVIEWS_SUCCESS';
export const FETCH_REVIEWS_ERROR = 'app/ListingPage/FETCH_REVIEWS_ERROR';

export const FETCH_LISTINGS_IN_REGION_REQUEST = 'app/ListingPage/FETCH_LISTINGS_IN_REGION_REQUEST';
export const FETCH_LISTINGS_IN_REGION_SUCCESS = 'app/ListingPage/FETCH_LISTINGS_IN_REGION_SUCCESS';
export const FETCH_LISTINGS_IN_REGION_ERROR = 'app/ListingPage/FETCH_LISTINGS_IN_REGION_ERROR';

export const FETCH_LINE_ITEMS_REQUEST = 'app/ListingPage/FETCH_LINE_ITEMS_REQUEST';
export const FETCH_LINE_ITEMS_SUCCESS = 'app/ListingPage/FETCH_LINE_ITEMS_SUCCESS';
export const FETCH_LINE_ITEMS_ERROR = 'app/ListingPage/FETCH_LINE_ITEMS_ERROR';

export const SEND_ENQUIRY_REQUEST = 'app/ListingPage/SEND_ENQUIRY_REQUEST';
export const SEND_ENQUIRY_SUCCESS = 'app/ListingPage/SEND_ENQUIRY_SUCCESS';
export const SEND_ENQUIRY_ERROR = 'app/ListingPage/SEND_ENQUIRY_ERROR';

// ================ Reducer ================ //

const initialState = {
  id: null,
  showListingError: null,
  reviews: [],
  reviewsMeta: {},
  fetchReviewsError: null,
  lineItems: null,
  fetchLineItemsInProgress: false,
  fetchLineItemsError: null,
  voucherValidation: null,
  aggregatedWalletBalance: null,
  remainingWalletBalance: null, // Remaining wallet after discount applied (null if no discount)
  sendEnquiryInProgress: false,
  sendEnquiryError: null,
  enquiryModalOpenForListingId: null,

  listingsInRegion: [],
  fetchListingsInRegionError: null,
  fetchListingsInRegionInProgress: false,
};

const listingPageReducer = (state = initialState, action = {}) => {
  const { type, payload } = action;
  switch (type) {
    case SET_INITIAL_VALUES:
      return { ...initialState, ...payload };

    case SHOW_LISTING_REQUEST:
      return { ...state, id: payload.id, showListingError: null };
    case SHOW_LISTING_ERROR:
      return { ...state, showListingError: payload };

    case FETCH_REVIEWS_REQUEST:
      return { ...state, fetchReviewsError: null };
    case FETCH_REVIEWS_SUCCESS:
      return {
        ...state,
        reviews: payload.concat ? [...state.reviews, ...payload.reviews] : payload.reviews,
        reviewsMeta: payload.reviewsMeta,
      };
    case FETCH_REVIEWS_ERROR:
      return { ...state, fetchReviewsError: payload };

    case FETCH_LISTINGS_IN_REGION_REQUEST:
      return { ...state, fetchListingsInRegionInProgress: true, fetchListingsInRegionError: null };
    case FETCH_LISTINGS_IN_REGION_SUCCESS:
      return { ...state, fetchListingsInRegionInProgress: false, listingsInRegion: payload };
    case FETCH_LISTINGS_IN_REGION_ERROR:
      return {
        ...state,
        fetchListingsInRegionInProgress: false,
        fetchListingsInRegionError: payload,
      };

    case FETCH_LINE_ITEMS_REQUEST:
      return { ...state, fetchLineItemsInProgress: true, fetchLineItemsError: null, voucherValidation: null, aggregatedWalletBalance: null, remainingWalletBalance: null };
    case FETCH_LINE_ITEMS_SUCCESS: {
      // When a voucher eligibility check fails the server returns data:[] to avoid
      // triggering a broken line-items calculation. Preserve the existing line items
      // so the session selection stays intact while VoucherInput shows the error.
      const hasVoucherError = payload.voucherValidation && !payload.voucherValidation.valid;
      const hasEmptyLineItems = !payload.lineItems || payload.lineItems.length === 0;
      return { 
        ...state, 
        fetchLineItemsInProgress: false, 
        lineItems: (hasVoucherError && hasEmptyLineItems) ? state.lineItems : payload.lineItems, 
        voucherValidation: payload.voucherValidation, 
        aggregatedWalletBalance: payload.aggregatedWalletBalance,
        remainingWalletBalance: (hasVoucherError && hasEmptyLineItems) ? state.remainingWalletBalance : payload.remainingWalletBalance,
      };
    }
    case FETCH_LINE_ITEMS_ERROR:
      return { ...state, fetchLineItemsInProgress: false, fetchLineItemsError: payload, voucherValidation: null, aggregatedWalletBalance: null, remainingWalletBalance: null };

    case SEND_ENQUIRY_REQUEST:
      return { ...state, sendEnquiryInProgress: true, sendEnquiryError: null };
    case SEND_ENQUIRY_SUCCESS:
      return { ...state, sendEnquiryInProgress: false };
    case SEND_ENQUIRY_ERROR:
      return { ...state, sendEnquiryInProgress: false, sendEnquiryError: payload };

    default:
      return state;
  }
};

export default listingPageReducer;

// ================ Action creators ================ //

export const setInitialValues = initialValues => ({
  type: SET_INITIAL_VALUES,
  payload: pick(initialValues, Object.keys(initialState)),
});

export const showListingRequest = id => ({
  type: SHOW_LISTING_REQUEST,
  payload: { id },
});

export const showListingError = e => ({
  type: SHOW_LISTING_ERROR,
  error: true,
  payload: e,
});

export const fetchReviewsRequest = () => ({ type: FETCH_REVIEWS_REQUEST });
export const fetchReviewsSuccess = reviews => ({ type: FETCH_REVIEWS_SUCCESS, payload: reviews });
export const fetchReviewsError = error => ({
  type: FETCH_REVIEWS_ERROR,
  error: true,
  payload: error,
});

export const fetchLineItemsRequest = () => ({ type: FETCH_LINE_ITEMS_REQUEST });
export const fetchLineItemsSuccess = lineItems => ({
  type: FETCH_LINE_ITEMS_SUCCESS,
  payload: lineItems,
});
export const fetchLineItemsError = error => ({
  type: FETCH_LINE_ITEMS_ERROR,
  error: true,
  payload: error,
});

export const sendEnquiryRequest = () => ({ type: SEND_ENQUIRY_REQUEST });
export const sendEnquirySuccess = () => ({ type: SEND_ENQUIRY_SUCCESS });
export const sendEnquiryError = e => ({ type: SEND_ENQUIRY_ERROR, error: true, payload: e });

export const fetchListingsInRegionRequest = () => ({ type: FETCH_LISTINGS_IN_REGION_REQUEST });
export const fetchListingsInRegionSuccess = listings => ({
  type: FETCH_LISTINGS_IN_REGION_SUCCESS,
  payload: listings,
});
export const fetchListingsInRegionError = error => ({
  type: FETCH_LISTINGS_IN_REGION_ERROR,
  error: true,
  payload: error,
});

// ================ Thunks ================ //

export const showListing = (listingId, isOwn = false) => (dispatch, getState, sdk) => {
  dispatch(showListingRequest(listingId));
  dispatch(fetchCurrentUser());
  const params = {
    id: listingId,
    include: ['author', 'author.profileImage', 'images'],
    'fields.image': [
      // Listing page
      'variants.landscape-crop',
      'variants.landscape-crop2x',
      'variants.landscape-crop4x',
      'variants.landscape-crop6x',

      // Social media
      'variants.facebook',
      'variants.twitter',

      // Image carousel
      'variants.scaled-small',
      'variants.scaled-medium',
      'variants.scaled-large',
      'variants.scaled-xlarge',

      // Avatars
      'variants.square-small',
      'variants.square-small2x',
    ],
  };

  const show = isOwn ? sdk.ownListings.show(params) : sdk.listings.show(params);

  return show
    .then(data => {
      dispatch(addMarketplaceEntities(data));
      return data;
    })
    .catch(e => {
      const error = storableError(e);
      dispatch(showListingError(error));
      // Re-throw error for SSR to handle with proper status code
      throw error;
    });
};

export const fetchReviews = (userId, fetchParam = {}, concat = true) => (
  dispatch,
  getState,
  sdk
) => {
  dispatch(fetchReviewsRequest());

  return sdk.reviews
    .query({
      subject_id: userId,
      type: 'ofProvider',
      perPage: REVIEWS_PAGE_SIZE,
      ...fetchParam,
      state: 'public',
      include: ['author', 'author.profileImage'],
      'fields.image': ['variants.square-small', 'variants.square-small2x'],
    })
    .then(response => {
      const reviews = denormalisedResponseEntities(response);
      const reviewsMeta = response.data.meta;

      dispatch(fetchReviewsSuccess({ reviews, reviewsMeta, concat }));
    })
    .catch(e => {
      dispatch(fetchReviewsError(storableError(e)));
    });
};

export const sendEnquiry = (listingId, message) => (dispatch, getState, sdk) => {
  dispatch(sendEnquiryRequest());

  let bodyParams = {
    transition: transitions.ENQUIRE,
    processAlias: defaultConfig.bookingProcessAlias,
    params: { listingId },
  };

  const partnerFromState = getState().ui.partner;
  if (partnerFromState) {
    const partner = getPartnerDataForEntites(partnerFromState);

    bodyParams = set(bodyParams, 'params.protectedData.partner', partner);
  }

  return sdk.transactions
    .initiate(bodyParams)
    .then(response => {
      const transactionId = response.data.data.id;

      // Send the message to the created transaction
      return sdk.messages.send({ transactionId, content: message }).then(() => {
        dispatch(sendEnquirySuccess());
        dispatch(fetchCurrentUserHasOrdersSuccess(true));
        return transactionId;
      });
    })
    .catch(e => {
      dispatch(sendEnquiryError(storableError(e)));
      throw e;
    });
};

// Helper function for loadData call.
const fetchMonthlyTimeSlots = (dispatch, listing) => {
  const attributes = listing.attributes;
  // Listing could be ownListing entity too, so we just check if attributes key exists
  const hasTimeZone =
    attributes && attributes.availabilityPlan && attributes.availabilityPlan.timezone;

  // Fetch sessions on both SSR and client side so that:
  //   - SSR: preloaded state already contains sessions → no empty-calendar flash on first paint
  //   - Client: re-fetches on mount to get fresh data (reducer keeps stale sessions visible
  //     during the in-flight request, so there is no blank intermediate state)
  if (listing.id && hasTimeZone) {
    const tz = listing.attributes.availabilityPlan.timezone;
    const firstMonth = new Date();

    const thirdMonth = resetToStartOfDay(new Date(), tz, dayCountAvailableForBooking);

    // skipClosedByFilter: for durations > 7 days the Crion API pre-marks
    // same-weekday overlapping sessions with closedBySessionIds at creation
    // time. Without this flag only the very first occurrence of each weekday
    // would appear in the booking calendar. Sessions blocked by real bookings
    // still get filtered by availableSeats / status.
    return dispatch(fetchSessions(listing.id, firstMonth, thirdMonth, 1, { skipClosedByFilter: true }));
  }

  // By default return an empty array
  return Promise.all([]);
};

export const fetchTransactionLineItems = ({ bookingData, listingId, isOwnListing }) => dispatch => {
  dispatch(fetchLineItemsRequest());
  transactionLineItems({ bookingData, listingId, isOwnListing })
    .then(response => {
      const { data: lineItems, voucherValidation, aggregatedWalletBalance, remainingWalletBalance } = response;
      dispatch(fetchLineItemsSuccess({ lineItems, voucherValidation, aggregatedWalletBalance, remainingWalletBalance }));
    })
    .catch(e => {
      dispatch(fetchLineItemsError(storableError(e)));
      log.error(e, 'fetching-line-items-failed', {
        listingId: listingId.uuid,
        bookingData: bookingData,
      });
    });
};

export const fetchListingsInRegion = (geoParams, listingId) => async (dispatch, getState, sdk) => {
  dispatch(fetchListingsInRegionRequest());

  const { partner } = getState().ui;
  const isSSR = typeof window === 'undefined';

  // SSR: Try to use cached listings first (updated daily at 2 AM)
  if (isSSR && geoParams?.lat && geoParams?.lng) {
    try {
      // Access globally available cache (set by server at startup)
      if (global.listingsCache) {
        const cachedListings = global.listingsCache.getListingsNearLocation(
          geoParams.lat,
          geoParams.lng,
          50 // 50km radius
        );
        
        // Filter out the current listing
        const filteredListings = cachedListings.filter(listing => listing.id.uuid !== listingId);
        
        if (filteredListings.length > 0) {
          console.log(
            `✅ Listing Page SSR: Using ${filteredListings.length} cached related listings near (${geoParams.lat}, ${geoParams.lng})`
          );
          dispatch(fetchListingsInRegionSuccess(filteredListings));
          return Promise.resolve(filteredListings);
        } else {
          console.log('⚠️ Listing Page SSR: Cache returned no nearby listings, falling back to live fetch');
        }
      } else {
        console.warn('⚠️ Listing Page SSR: Cache not available, falling back to live fetch');
      }
    } catch (error) {
      console.error('❌ Listing Page SSR: Cache error, falling back to live fetch:', error.message);
    }
  }

  // Client-side OR SSR fallback: Fetch from API
  // Try to generate bounding box around the given geolocation. On client
  // this uses mapbox's LngLat.toBounds via locationBounds(). On server
  // window.mapboxgl is not available so locationBounds() returns null.
  // In that case approximate bounds using spherical math so SSR still
  // applies the same location filtering.
  let bounds = locationBounds(geoParams, 50000);
  if (!bounds && geoParams && typeof geoParams.lat === 'number' && typeof geoParams.lng === 'number') {
    // Approximate bounds (in degrees) for given distance in meters
    const EARTH_RADIUS = 6371000; // meters
    const DEG_TO_RAD = Math.PI / 180.0;
    const RAD_TO_DEG = 180.0 / Math.PI;
    const distance = 50000; // meters
    const lat = Number(geoParams.lat);
    const lng = Number(geoParams.lng);

    // latitude delta (in degrees)
    const deltaLat = (distance / EARTH_RADIUS) * RAD_TO_DEG;
    // longitude delta depends on latitude
    const deltaLng = (distance / EARTH_RADIUS) * RAD_TO_DEG / Math.cos(lat * DEG_TO_RAD);

    bounds = {
      ne: { lat: lat + deltaLat, lng: lng + deltaLng },
      sw: { lat: lat - deltaLat, lng: lng - deltaLng },
    };
  }

  const publishedListings = { 'attributes.state': { $eq: LISTING_STATE_PUBLISHED } };

  const geoLocationMaybe = getLocationQuery(bounds);

  const newParams = {
    ...publishedListings,
    ...geoLocationMaybe,
  };

  return api.listings
    .fetchListings(newParams, partner?.key)
    .then(res => {
      const { items } = res.data;
      const listings = items.map(listing => parseListingToSharetribe(listing, listing.author));

      // filter shown listing from listings in region
      const filterListings = listings.filter(listing => listing.id.uuid !== listingId);

      dispatch(fetchListingsInRegionSuccess(filterListings));
    })
    .catch(e => {
      dispatch(fetchListingsInRegionError(e));
      throw e;
    });
};

export const loadData = (params) => async (dispatch, getState) => {
  const isSSR = typeof window === 'undefined';
  if (isSSR) {
    console.log('===== SSR: ListingPage loadData START =====');
    console.log('  Params:', params);
    console.log('  ID:', params.id);
    console.log('  Type:', params.type);
    console.log('  Slug:', params.slug);
  }
  
  // ✅ VALIDATE UUID: Reject invalid listing IDs immediately with 404
  // UUID format: xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx (8-4-4-4-12 hex digits)
  // ⚠️ ONLY ON SSR: Don't throw errors during client-side navigation (locale switching)
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  
  if (isSSR && (!params.id || !uuidRegex.test(params.id))) {
    // SSR: Throw error for invalid UUID to return 404 response
    console.log('===== SSR: INVALID UUID → 404 =====');
    console.log('  Received ID:', params.id);
    console.log('  This is not a valid listing ID (must be UUID format)');
    console.log('  Likely cause: URL typo or malformed listing URL');
    console.log('  → Throwing 404 error');
    const error = new Error(`Invalid listing ID: ${params.id}`);
    error.status = 404;
    throw error;
  }
  
  // Client-side: Silently skip if invalid UUID (shouldn't happen in normal flow)
  if (!isSSR && (!params.id || !uuidRegex.test(params.id))) {
    console.warn('Client-side: Invalid listing ID, skipping loadData');
    return null;
  }
  
  const listingId = new UUID(params.id);
  if (isSSR) {
    console.log('  Valid UUID confirmed');
  }
  
  // Check if this is a pending approval, draft, or closed variant (own listing)
  const isOwnListing = params.variant === LISTING_PAGE_PENDING_APPROVAL_VARIANT || 
                       params.variant === LISTING_PAGE_DRAFT_VARIANT || 
                       params.variant === LISTING_PAGE_CLOSED_VARIANT;
  
  let result;
  try {
    if (isOwnListing) {
      // For variants (pending-approval/draft/closed), always use own listings API
      result = await dispatch(showListing(listingId, true));
    } else {
      // For regular listings, try public API first
      result = await dispatch(showListing(listingId, false));
      
      // If public API fails (e.g., listing is closed), try owner API as fallback
      if (!result?.data?.data) {
        try {
          result = await dispatch(showListing(listingId, true));
        } catch (fallbackError) {
          // If both fail, throw the original error
          throw fallbackError;
        }
      }
    }
  } catch (fetchError) {
    // Handle listing fetch errors with proper status codes for SSR
    if (isSSR) {
      // Convert SDK errors to proper HTTP status codes
      const statusCode = fetchError.status || fetchError.statusCode || 404;
      
      const error = new Error(fetchError.message || 'Listing not found');
      error.status = statusCode;
      throw error;
    }
    // On client side, let the error be handled by Redux state
    throw fetchError;
  }

  if (result?.data?.data) {
    // Denormalize the listing to include author relationship
    // This is needed for title generation which requires author name
    const denormalizedListings = denormalisedResponseEntities(result);
    const listing = denormalizedListings[0] || result.data.data;
    const { partnerApproved } = listing?.attributes?.metadata || {}; 

    if (isSSR) {
      let hostname = params.hostname;
      let partnerKey = null;
      let isPartnerSubdomain = false;
      let parts = [];

      if (hostname) {
        parts = hostname.split('.');
        // Core domains: crion.org, test.crion.org, localhost
        const isCoreDomain =
          (parts.length === 2 && parts[0] === 'crion' && parts[1] === 'org') ||
          (parts.length === 3 && parts[0] === 'test' && parts[1] === 'crion' && parts[2] === 'org') ||
          (parts.length === 1 && parts[0] === 'localhost');

        if (!isCoreDomain) {
          // Any other subdomain (including bad.test.crion.org, partner.crion.org, test.test.crion.org, etc.)
          partnerKey = parts[0];
          isPartnerSubdomain = true;
        } else {
          isPartnerSubdomain = false;
          partnerKey = null;
        }

        // Handle partner subdomains
        if (isPartnerSubdomain) {
          // Check if this subdomain exists in partnerApproved metadata
          const hasPartnerApprovalData = partnerApproved && partnerKey in partnerApproved;
          
          if (!hasPartnerApprovalData) {
            // Subdomain not recognized, return 410 Gone
            const error = new Error('Subdomain not recognized');
            error.status = 410;
            throw error;
          }
          
          // Check if listing is approved for this subdomain
          if (partnerApproved[partnerKey] !== 'approved') {
            // Listing not approved for this subdomain, return 410 Gone
            const error = new Error('Listing not approved for subdomain');
            error.status = 410;
            throw error;
          }
        }
      }
      
      // ✅ STRICT URL VALIDATION: Enforce single canonical URL per listing
      // This prevents duplicate URLs (wrong slugs/types) from being crawled/indexed
      // Only the dynamically generated slug (new format) is allowed to render
      if (!isOwnListing && params.originalUrl && params.type && params.slug) {
        const { createSlug } = require('../../util/urlHelpers');
        const { getTranslatedListingType } = require('../../util/listingHelpers');
        const { getLocaleFromUrl, LOCALE_TO_LANG } = require('../../constants/locales');
        const ListingService = require('../../services/ListingService').default;
        
        // Extract locale from URL path
        const locale = getLocaleFromUrl(params.originalUrl);
        
        if (locale) {
          const language = LOCALE_TO_LANG[locale] || 'english';
          const { publicData } = listing.attributes || {};
          const { type } = publicData || {};
          
          // Get intl from Redux state for dynamic title generation
          const state = getState();
          const intl = state.intl;
          
          // ✅ SINGLE SOURCE OF TRUTH: Generate correct slug dynamically using new format
          // This ensures we always use: "{discipline} in {location} with {author}"
          // Instead of relying on potentially outdated stored titles
          let correctSlug = null;
          try {
            const generatedTitle = ListingService.createTitle(intl, listing, language, params.originalUrl);
            correctSlug = generatedTitle ? createSlug(generatedTitle) : null;
          } catch (error) {
            // Fallback: If dynamic generation fails, listing should still work
            // but we won't enforce strict slug validation
            correctSlug = null;
          }
          
          // Get correct type for this locale
          const correctType = getTranslatedListingType(type, locale);
          
          // Decode URL parameters to handle special characters (e.g., leçon -> le%C3%A7on)
          const urlType = decodeURIComponent(params.type);
          const urlSlug = decodeURIComponent(params.slug);
          
          // ✅ STRICT VALIDATION: Both type AND slug must match exactly
          // No fallback allowed - this ensures only one URL per listing renders
          const typeMatches = urlType === correctType;
          const slugMatches = correctSlug ? urlSlug === correctSlug : true; // Only validate if we have a slug
          
          if (!typeMatches || !slugMatches) {
            // Construct correct URL with dynamically generated slug
            const pathParts = params.originalUrl.split('?');
            const queryString = pathParts[1] ? `?${pathParts[1]}` : '';
            
            // Build correct URL path: /:lang/:type/:slug/:id
            // Always use the dynamically generated slug (new format)
            const correctPath = `/${locale}/${correctType}/${correctSlug || urlSlug}/${params.id}`;
            const correctUrl = correctPath + queryString;
            
            const error = new Error('Redirect to correct localized URL');
            error.status = 301;
            error.redirectUrl = correctUrl;
            throw error;
          }
        }
      }
    }
  }

  // ✅ FETCH SESSIONS: Load time slots for booking calendar
  // This ensures the booking panel has availability data to display
  if (result?.data?.data) {
    const listing = result.data.data;

    // During SSR, also fetch related listings in the same region so that
    // related listings are available in the serialized preloaded state
    // and can be rendered into the server HTML (hidden nav / crawler links).
    try {
      const isSSR = typeof window === 'undefined';
      const geo = listing.attributes && listing.attributes.geolocation;
      if (isSSR && geo) {
        // params.id is the UUID string of the current listing
        await dispatch(fetchListingsInRegion(geo, params.id));
      }
    } catch (err) {
      // Log the error but don't fail the whole page render
      log.error(err, 'fetch-listings-in-region-failed', { listingId: params.id });
    }

    return fetchMonthlyTimeSlots(dispatch, listing);
  }

  return null;
};